import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { useFormattedDate, useFormattedTime } from '@micromag/core/hooks';
import { useRoutePush } from '@micromag/core/contexts';
import { Button } from '@micromag/core/components';

import useStoryVersionCreate from '../../hooks/useStoryVersionCreate';
import { useOrganisation } from '../../contexts/OrganisationContext';
import * as AppPropTypes from '../../lib/PropTypes';

const propTypes = {
    story: MicromagPropTypes.story.isRequired,
    version: AppPropTypes.version.isRequired,
    current: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    current: false,
    className: null,
};

const StoryVersionMenubar = ({ story, version, current, className }) => {
    const push = useRoutePush();

    const getDate = useFormattedDate();
    const getTime = useFormattedTime();

    // @TODO verify permissions
    const organisation = useOrganisation();
    const { permissions: organisationPermissions = null } = organisation || {};
    const { can_edit_story: organisationCanEditStory = false } = organisationPermissions || {};
    const canEditStory = organisation === null || organisationCanEditStory;

    const {
        created_at: createdAt = null,
        components = [],
        theme = null,
        medias = null,
    } = version || {};
    const date = createdAt !== null ? getDate(createdAt) : null;
    const time = createdAt !== null ? getTime(createdAt) : null;

    const { id: storyId } = story || {};

    const { create: createStoryVersion, creating } = useStoryVersionCreate(storyId);

    const onEdit = useCallback(() => {
        push('stories.editor', { story: storyId });
    }, [push, storyId]);

    const onRestoreVersion = useCallback(() => {
        const versionData = {
            components,
            theme,
            medias,
        };
        createStoryVersion(versionData).then(onEdit);
    }, [createStoryVersion, onEdit, components, theme, medias]);

    const onButtonClick = useCallback(() => {
        if (current) {
            onEdit();
        } else {
            onRestoreVersion();
        }
    }, [current, onRestoreVersion]);

    const savingRestoreMessage = (
        <FormattedMessage defaultMessage="Restoring..." description="Restoring... label" />
    );
    const restoreMessage = (
        <FormattedMessage
            defaultMessage="Restore this version"
            description="Restore this version label"
        />
    );
    const editMessage = <FormattedMessage defaultMessage="Edit" description="Edit label" />;
    const finalRestoreMessage = creating ? savingRestoreMessage : restoreMessage;

    return (
        <div
            className={classNames([
                'row',
                'align-items-center',
                {
                    [className]: className !== null,
                },
            ])}
        >
            <div className="col-md">
                <span>{`${date} - ${time}`}</span>
                {current ? (
                    <>
                        <span>{` (`}</span>
                        <FormattedMessage
                            defaultMessage="Current version"
                            description="Current version label"
                        />
                        <span>)</span>
                    </>
                ) : null}
            </div>
            {canEditStory ? (
                <div className="col-auto mt-2 mt-md-0">
                    <Button className="btn-primary" onClick={onButtonClick}>
                        {current ? editMessage : finalRestoreMessage}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

StoryVersionMenubar.propTypes = propTypes;
StoryVersionMenubar.defaultProps = defaultProps;

export default StoryVersionMenubar;
